import { useCallback, useEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Box, MenuItem, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { find, includes } from 'lodash';
import NgpSelect from '../../../form/NgpSelect';
import { useDataService } from '../../../../services/ApiDataService';
import useRecordContext from '../../../../utilities/RecordContextProviderUtilities';
import useNgpFormContext from '../../../../utilities/NgpFormContextProviderUtilities';
import { useDeviceInfraContext } from './DeviceInfraContext';
import {
    DEVICE_INTERFACES_PATH,
    DEVICE_IP_COLLECTORS_PATH,
    DEVICE_QUERY_BASE_URL,
    LABEL_MONITORING_IP,
    USER_SELECTED_MONITORING_IP_FIELD,
} from './DeviceConstants';
import { AUTOMATIC_TYPE, buildSelectableIps, MANUAL_TYPE } from '../services/DeviceIpSelectorService';
import { SUB_TYPE_VALUES } from '../../../filters/virtual-infrastructure-type-filter/SubTypeHelper';
import { isNewState } from './DeviceHelper';

// Server subtypes classified as virtual infrastructure (cannot change monitoring IP)
const VIRTUAL_SUB_TYPES = SUB_TYPE_VALUES;

const automaticLabel = i18n._(t`Automatic Selection`);
const collectorLabel = i18n._(t`Collector`);
const manualLabel = i18n._(t`Manual`);

const useStyles = makeStyles((theme) => ({
    optionTitle: {
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    optionDetail: {
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    optionValue: {
        fontWeight: 500,
    },
    interfaceType: {
        fontSize: '0.85em',
    },
    discoveredBadge: {
        fontStyle: 'italic',
    },
    menuItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        minHeight: 48,
        paddingTop: 4,
        paddingBottom: 4,
    },
}));

/**
 * Renders a two-line Automatic option for the dropdown.
 * Line 1: "Automatic"
 * Line 2: "{ip} ({interfaceType}) via {collectorName}"
 */
const AutomaticOptionContent = ({ option, classes }) => (
    <div>
        <div className={classes.optionTitle}>
            <span className={classes.optionValue}>{automaticLabel}</span>
        </div>
        <div className={classes.optionDetail}>
            <span className={classes.optionValue}>{option.ipAddress}</span>
            {option.interfaceType && <span className={classes.interfaceType}>{` (${option.interfaceType})`}</span>}
            {option.collectorName && (
                <>
                    <span>{' via '}</span>
                    <span className={classes.optionValue}>{option.collectorName}</span>
                </>
            )}
        </div>
    </div>
);

/**
 * Renders a two-line manual IP option for the dropdown.
 * Line 1: "{ip} ({interfaceType}) - Discovered"
 * Line 2: "Collector: {collectorName}"
 */
const ManualOptionContent = ({ option, classes }) => (
    <div>
        <div className={classes.optionTitle}>
            <span className={classes.optionValue}>{option.ipAddress}</span>
            {option.interfaceType && <span className={classes.interfaceType}>{` (${option.interfaceType})`}</span>}
            {option.discovered && (
                <Tooltip title={<Trans>This IP address was discovered during polling</Trans>}>
                    <span className={classes.discoveredBadge}>
                        {' - '}
                        <Trans>Discovered</Trans>
                    </span>
                </Tooltip>
            )}
        </div>
        {option.collectorName && (
            <div className={classes.optionDetail}>
                <span>
                    {collectorLabel}
                    {': '}
                </span>
                <span className={classes.optionValue}>{option.collectorName}</span>
            </div>
        )}
    </div>
);

/**
 * Monitoring IP dropdown selector for device detail page.
 * Fetches available IPs from collectors and interfaces APIs,
 * displays "Automatic" + manual IP options with two-line formatting.
 * Binds to userSelectedMonitoringIp form field.
 *
 * Disabled for: new devices, access points, virtual infrastructure, or while loading.
 * Matches portal/ui DeviceIPSelectorComponentController behavior.
 */
const DeviceIpSelector = () => {
    const classes = useStyles();
    const dataService = useDataService();
    const { id: deviceId } = useRecordContext();
    const { formData } = useNgpFormContext();
    const { isCreateAction, isAccessPoint, isServer } = useDeviceInfraContext();
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const hasDeviceOverrideRef = useRef(undefined);
    const loopbackIpsRef = useRef([]);

    const defaultIp = formData?.ipAddress || '';
    const currentSelectedIp = formData?.userSelectedMonitoringIp;

    // Virtual infrastructure cannot change monitoring IP (portal/ui: isVirtualInfrastructure)
    const isVirtual = isServer && includes(VIRTUAL_SUB_TYPES, formData?.subType);
    const isNew = isNewState(formData?.state);

    // Capture initial device override state once, before effect runs (portal/ui: $onChanges)
    if (hasDeviceOverrideRef.current === undefined && defaultIp) {
        hasDeviceOverrideRef.current = !!currentSelectedIp;
    }

    // Keep loopback IPs ref in sync with form data (stable reference for effect)
    if (formData?.loopbackIps) {
        loopbackIpsRef.current = formData.loopbackIps;
    }

    useEffect(() => {
        if (!deviceId || isCreateAction) {
            setLoading(false);
            return;
        }

        const collectorsUrl = `${DEVICE_QUERY_BASE_URL}/${deviceId}/${DEVICE_IP_COLLECTORS_PATH}`;
        const interfacesUrl = `${DEVICE_QUERY_BASE_URL}/${deviceId}/${DEVICE_INTERFACES_PATH}`;

        Promise.all([dataService.get(collectorsUrl), dataService.get(interfacesUrl)])
            .then(([metadata, interfaces]) => {
                setOptions(
                    buildSelectableIps(
                        defaultIp,
                        metadata?.data || [],
                        interfaces?.data || [],
                        loopbackIpsRef.current,
                        hasDeviceOverrideRef.current ?? false
                    )
                );
            })
            .catch(() => {
                // portal/ui: on error, selectableIps stays empty → selector disabled
                setOptions([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [dataService, deviceId, defaultIp, isCreateAction]);

    /**
     * Renders the closed-state display for the selected value.
     * Automatic: "{ip} ({interfaceType})"
     * Manual: "Manual: {ip} ({interfaceType})"
     * Matches portal/ui getSelectedIpAddress() behavior.
     */
    const renderSelectedValue = useCallback(
        (selectedValue) => {
            const match = find(options, { value: selectedValue });
            if (!match) {
                return defaultIp || '--';
            }

            const ipPart = match.ipAddress || '';
            const ifTypePart = match.interfaceType ? ` (${match.interfaceType})` : '';
            const ipIfType = `${ipPart}${ifTypePart}`;

            if (match.type === AUTOMATIC_TYPE) {
                return ipIfType;
            }
            return `${manualLabel}: ${ipIfType}`;
        },
        [options, defaultIp]
    );

    // portal/ui: !isVirtual && isValidType && !isNew && hasIpMetadata()
    const hasIpMetadata = options.length > 0;

    // Build menu items with placeholder for current value during loading
    const menuItems = hasIpMetadata ? options : [];

    const isDisabled = isCreateAction || isAccessPoint || isVirtual || isNew || loading || !hasIpMetadata;

    // If loading, and we have a selected value not in options, add placeholder to prevent warning
    if (loading && currentSelectedIp && !find(menuItems, { value: currentSelectedIp })) {
        menuItems.push({
            value: currentSelectedIp,
            ipAddress: currentSelectedIp,
            type: MANUAL_TYPE,
            interfaceType: '',
            collectorName: '',
            discovered: false,
        });
    }

    return (
        <Box display="flex" alignItems="flex-end">
            <NgpSelect
                fieldName={USER_SELECTED_MONITORING_IP_FIELD}
                label={LABEL_MONITORING_IP}
                disabled={isDisabled}
                selectProps={{ renderValue: renderSelectedValue, displayEmpty: true }}
                inputLabelProps={{ shrink: true }}
            >
                {menuItems.map((option) => (
                    <MenuItem key={option.value || '__auto__'} value={option.value} className={classes.menuItem}>
                        {option.type === AUTOMATIC_TYPE ? (
                            <AutomaticOptionContent option={option} classes={classes} />
                        ) : (
                            <ManualOptionContent option={option} classes={classes} />
                        )}
                    </MenuItem>
                ))}
            </NgpSelect>
            <Tooltip
                title={i18n._(
                    t`Changes the IP address used to monitor this device. Wireless APs and devices currently being classified cannot change IP. Note that an IP change may take a few minutes to propagate through the system.`
                )}
            >
                <HelpOutlineIcon fontSize="small" color="action" style={{ marginLeft: 4, marginBottom: 4 }} />
            </Tooltip>
        </Box>
    );
};

export default DeviceIpSelector;
